import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import type { Entity } from '../models/entity.model';
import { environment } from '../../../environments/environment';
import type { EntitiesPaged } from '../models/entities-paged.model';
import type { EntityEditorPropertyChangedEvent } from '../../entity-editor/models/entity-editor-property-changed.event';
import type { Group } from '../models/group.model';
import type { Property } from '../models/property.model';

export interface PropertyUpdate {
  Key: string;
  Value: unknown;
}

/**
 * Mapper function which converts an API response entity into a client state entity. Needed to convert the group and property
 * lists into a more client-friendly dictionaries.
 * @param model
 */
const apiModelToEntity = (model: EntityApiModel): Entity => {
  const properties: Record<string, Property> = {};
  const groups: Record<string, Group> = {};

  model.Groups.forEach(group => {
    groups[group.Key] = group;
  });

  model.Properties.forEach(property => {
    properties[property.Key] = property;
  });

  return {
    ...model,
    Groups: groups,
    Properties: properties
  };
};

/**
 * Helper for converting a list of entity API models into a list of client entities. Uses apiModelToEntity internally.
 * @param models
 */
const apiModelListToEntityList = (models: EntityApiModel[]): Entity[] => {
  return models ? models.map(apiModelToEntity) : [];
};

@Injectable({
  providedIn: 'root'
})
export class EntityService {
  constructor(private http: HttpClient) {}

  /**
   * Gets an entity and all it's children as a flat list
   * @param projectId Project that entity belongs to
   */
  public getEntityTree(projectId: string): Observable<Entity[]> {
    return this.http.get<EntityApiModel[]>(`${environment.apiUrl}/entity/${projectId}`).pipe(map(apiModelListToEntityList));
  }

  queryEntities(searchPhrase: string, page: number, pageSize: number): Observable<EntitiesPaged> {
    return this.http
      .get<EntitiesPagedApiModel>(`${environment.apiUrl}/projects/search?page=${page}&pageSize=${pageSize}&searchString=${searchPhrase}`)
      .pipe(
        map(pagedResult => {
          return <EntitiesPaged>{
            ...pagedResult,
            Items: apiModelListToEntityList(pagedResult.Items)
          };
        })
      );
  }

  updateEntity(
    projectId: string,
    entityId: string,
    propertyKey: string,
    value: unknown,
    measurementUnit: string = null
  ): Observable<Entity[]> {
    return this.http
      .patch<EntityApiModel[]>(`${environment.apiUrl}/entity/${projectId}/${entityId}`, [
        {
          Key: propertyKey,
          Value: value,
          MeasurementUnit: measurementUnit
        }
      ])
      .pipe(map(apiModelListToEntityList));
  }

  updateEntityProperties(projectId: string, entityId: string, properties: EntityEditorPropertyChangedEvent[]): Observable<Entity[]> {
    return this.http
      .patch<EntityApiModel[]>(
        `${environment.apiUrl}/entity/${projectId}/${entityId}`,
        properties.map(p => ({
          Key: p.propertyKey,
          Value: p.value,
          MeasurementUnit: p.measurementUnit
        }))
      )
      .pipe(map(apiModelListToEntityList));
  }

  createOrUpdateTemporaryEntity(entityTypeKey: string, properties: PropertyUpdate[]): Observable<Entity[]> {
    return this.http
      .post<EntityApiModel[]>(`${environment.apiUrl}/entity/temporary/${entityTypeKey}`, properties || [])
      .pipe(map(apiModelListToEntityList));
  }

  multiProductUpdateEntity(
    projectId: string,
    multiEditIds: string[],
    propertyChange: EntityEditorPropertyChangedEvent
  ): Observable<Entity[]> {
    const body = {
      EntityIds: multiEditIds,
      Properties: [
        {
          Key: propertyChange.propertyKey,
          Value: propertyChange.value,
          MeasurementUnit: propertyChange.measurementUnit
        }
      ]
    };

    return this.http.patch<EntityApiModel[]>(`${environment.apiUrl}/entity/${projectId}/batch`, body).pipe(map(apiModelListToEntityList));
  }
}

/**
 * API response model for entity, not that groups & properties are an array as opposed to the client model where they're dictionaries.
 */
interface EntityApiModel {
  Id: string;
  EntityTypeKey: string;
  ParentId?: string;
  Groups: Group[];
  Properties: Property[];
  DateCreated?: string;
  DateModified?: string;
}

/**
 * API response model for paged entities
 */
interface EntitiesPagedApiModel {
  Page: number;
  PageSize: number;
  TotalItems: number;
  Items: EntityApiModel[];
}
